import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { getSDK } from '../services/sdk-instance';
import { holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';
import type {
  ListProvidersResponse,
  ListProviderModelsResponse,
  ModelCatalogEntry,
  CreateCatalogEntryParams,
  UpdateCatalogEntryParams,
  EntryReferencesResponse,
  ReassignEntryParams,
  ReassignEntryResponse,
  SyncResult,
  ProviderType,
  DiscoverProviderResult,
  SupportedParamSpec,
  ProviderCredentialStatus,
  ByokMode,
  ParamSchema,
  DefaultOverridesResult,
  VisionModelInfo,
} from '@localagent/sdk';

async function invalidateCatalogQueries(queryClient: QueryClient): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ['providers'] }),
    queryClient.invalidateQueries({ queryKey: ['provider-models'] }),
    queryClient.invalidateQueries({ queryKey: ['agent-options'] }),
    // The vision notice renders the resolved model's catalog labels, so a
    // capability write or a sync changes its verdict.
    queryClient.invalidateQueries({ queryKey: ['vision-model'] }),
  ]);
}

export function useProviders() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<ListProvidersResponse, Error>({
    queryKey: ['providers'],
    queryFn: () => getSDK().persona.listProviders(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useProviderModels(providerType: string | null) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<ListProviderModelsResponse, Error>({
    queryKey: ['provider-models', providerType],
    queryFn: () => getSDK().persona.listProviderModels(providerType!),
    enabled: Boolean(isAuthenticated && sdkConfigured && providerType),
  });
}

// The platform's image-reading model. Read-only by construction — there is no
// setter in the SDK (persona ADR-c7b9e).
export function useVisionModel() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<VisionModelInfo, Error>({
    queryKey: ['vision-model'],
    queryFn: () => getSDK().persona.getVisionModel(),
    enabled: Boolean(isAuthenticated && sdkConfigured),
  });
}

export function useEntryReferences(
  providerType: string | null,
  name: string | null,
  enabled: boolean,
) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<EntryReferencesResponse, Error>({
    queryKey: ['entry-references', providerType, name],
    queryFn: () => getSDK().persona.listEntryReferences(providerType!, name!),
    enabled: Boolean(isAuthenticated && sdkConfigured && providerType && name && enabled),
  });
}

export function useCreateCatalogEntry() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<ModelCatalogEntry, Error, { providerType: string; params: CreateCatalogEntryParams }>({
    mutationFn: ({ providerType, params }) =>
      getSDK().persona.createCatalogEntry(providerType, params),
    onSuccess: async () => {
      if (isAuthenticated) await invalidateCatalogQueries(queryClient);
    },
  });
}

export function useUpdateCatalogEntry() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<ModelCatalogEntry, Error, { providerType: string; params: UpdateCatalogEntryParams }>({
    mutationFn: ({ providerType, params }) =>
      getSDK().persona.updateCatalogEntry(providerType, params),
    onSuccess: async () => {
      if (isAuthenticated) await invalidateCatalogQueries(queryClient);
    },
  });
}

export function useDeleteCatalogEntry() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<void, Error, { providerType: string; name: string }>({
    mutationFn: ({ providerType, name }) =>
      getSDK().persona.deleteCatalogEntry(providerType, name),
    onSuccess: async () => {
      if (isAuthenticated) await invalidateCatalogQueries(queryClient);
    },
  });
}

export function useReassignEntryReferences() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<ReassignEntryResponse, Error, { providerType: string; params: ReassignEntryParams }>({
    mutationFn: ({ providerType, params }) =>
      getSDK().persona.reassignEntryReferences(providerType, params),
    onSuccess: () => {
      if (isAuthenticated) {
        void invalidateCatalogQueries(queryClient);
        void queryClient.invalidateQueries({ queryKey: ['agent-definitions'] });
        void queryClient.invalidateQueries({ queryKey: ['agent-definition-by-id'] });
        void queryClient.invalidateQueries({ queryKey: ['agent-definition-config-by-id'] });
      }
    },
  });
}

export function useSetProviderParams() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<void, Error, { providerType: string; params: Record<string, SupportedParamSpec> }>({
    mutationFn: ({ providerType, params }) =>
      getSDK().persona.setProviderParams(providerType, params),
    onSuccess: async () => {
      if (isAuthenticated) await invalidateCatalogQueries(queryClient);
    },
  });
}

/**
 * Write model-tier default VALUES.
 *
 * SEPARATE from `useSetProviderParams`, which writes the capability SPEC. Before
 * the split they were one write through one route and discovery OWNED the blob: a
 * catalog Sync overwrote `supported_params` wholesale and erased a tuned default
 * with it. `default_params` survives a sync by construction.
 *
 * `fingerprint` is the schema the caller rendered from. A stale one comes back
 * 409 `catalog_schema_stale` rather than a param-level rejection — which is what
 * makes "the editor cannot disagree with the resolver" a checkable claim rather
 * than a hope. The route returns the RE-RESOLVED schema, so the
 * next write echoes a current fingerprint instead of the one it just invalidated.
 *
 * A `null` value CLEARS that key back to the provider tier — which is
 * why the payload is `unknown` rather than a value type.
 */
export function useSetModelParamDefaults() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<
    ParamSchema,
    Error,
    {
      providerType: string;
      modelName: string;
      defaults: Record<string, unknown>;
      fingerprint: string;
    }
  >({
    mutationFn: ({ providerType, modelName, defaults, fingerprint }) =>
      getSDK().persona.setModelParamDefaults(providerType, modelName, defaults, fingerprint),
    onSuccess: async () => {
      if (isAuthenticated) {
        await Promise.all([
          invalidateCatalogQueries(queryClient),
          queryClient.invalidateQueries({ queryKey: ['param-schema'] }),
        ]);
      }
    },
  });
}

/**
 * Write provider-tier default VALUES.
 *
 * Same split as `useSetModelParamDefaults`: values are user-owned and survive a
 * catalog Sync; the capability SPEC is a separate write. Open to any account
 * member — the split is conceptual (preference vs constraint), not a permission
 * boundary.
 *
 * Omitting `modelName` on the impact query is load-bearing: a provider-tier
 * change reaches every model on the provider, so the blast radius is every
 * agent that pins the changed keys on any of those models.
 */
export function useSetProviderParamDefaults() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<
    ParamSchema,
    Error,
    {
      providerType: string;
      defaults: Record<string, unknown>;
      fingerprint: string;
    }
  >({
    mutationFn: ({ providerType, defaults, fingerprint }) =>
      getSDK().persona.setProviderParamDefaults(providerType, defaults, fingerprint),
    onSuccess: async () => {
      if (isAuthenticated) {
        await Promise.all([
          invalidateCatalogQueries(queryClient),
          queryClient.invalidateQueries({ queryKey: ['param-schema'] }),
        ]);
      }
    },
  });
}

/**
 * Which agents PIN these params, and will therefore NOT receive a defaults
 * change.
 *
 * Under sparse pins that is the whole blast radius: every unpinned agent picks
 * the new value up on its next turn, so the interesting set is exactly the ones
 * that will not. It is asked BEFORE the write, because the point is that the
 * user learns the impact while they can still decide.
 *
 * Disabled with no params — "the impact of changing nothing" is not a question
 * worth a round trip.
 */
export function useDefaultOverrides(
  providerType: string | undefined,
  params: string[],
  modelName?: string,
) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const sortedParams = [...params].sort();

  return useQuery<DefaultOverridesResult, Error>({
    queryKey: ['default-overrides', providerType, modelName, sortedParams.join(',')],
    queryFn: () => getSDK().persona.listDefaultOverrides(providerType!, sortedParams, modelName),
    enabled: Boolean(isAuthenticated && sdkConfigured && providerType && sortedParams.length > 0),
  });
}

export function useSyncCatalog() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<SyncResult, Error, void>({
    mutationFn: () => getSDK().persona.syncCatalog(),
    onSuccess: async () => {
      if (isAuthenticated) await invalidateCatalogQueries(queryClient);
    },
  });
}

export function useDiscoverProvider() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<
    DiscoverProviderResult,
    Error,
    { provider: ProviderType; deviceId?: string; modelIds?: string[] }
  >({
    mutationFn: ({ provider, deviceId, modelIds }) =>
      getSDK().persona.discoverProviderModels(provider, { deviceId, modelIds }),
    onSuccess: async (result) => {
      if (isAuthenticated && result.imported > 0) await invalidateCatalogQueries(queryClient);
    },
  });
}

function seedCredentialStatus(
  queryClient: QueryClient,
  provider: ProviderType,
  status: ProviderCredentialStatus,
): void {
  queryClient.setQueryData(['provider-credential', provider], status);
  void queryClient.invalidateQueries({ queryKey: ['providers'] });
}

export function useProviderCredentialStatus(provider: ProviderType | null, enabled = true) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);

  return useQuery<ProviderCredentialStatus, Error>({
    queryKey: ['provider-credential', provider],
    queryFn: () => getSDK().persona.getProviderCredentialStatus(provider as ProviderType),
    enabled: enabled && isAuthenticated && sdkConfigured && provider !== null,
  });
}

export function useSetProviderCredential() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<ProviderCredentialStatus, Error, { provider: ProviderType; apiKey: string }>({
    mutationFn: ({ provider, apiKey }) => getSDK().persona.setProviderCredential(provider, apiKey),
    onSuccess: (status, { provider }) => {
      if (isAuthenticated) seedCredentialStatus(queryClient, provider, status);
    },
  });
}

export function useDeleteProviderCredential() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<ProviderCredentialStatus, Error, { provider: ProviderType }>({
    mutationFn: ({ provider }) => getSDK().persona.deleteProviderCredential(provider),
    onSuccess: (status, { provider }) => {
      if (isAuthenticated) seedCredentialStatus(queryClient, provider, status);
    },
  });
}

/** Set the per-provider key-resolution policy (context ADR-032). */
export function useSetByokMode() {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const queryClient = useQueryClient();

  return useMutation<ProviderCredentialStatus, Error, { provider: ProviderType; mode: ByokMode }>({
    mutationFn: ({ provider, mode }) => getSDK().persona.setByokMode(provider, mode),
    onSuccess: (status, { provider }) => {
      if (isAuthenticated) seedCredentialStatus(queryClient, provider, status);
    },
  });
}
